import { useMemo } from 'react';
import {
  Select,
  SelectActionItem,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectMulti,
  SelectMultiContent,
  SelectMultiGroup,
  SelectMultiItem,
  SelectMultiTrigger,
  SelectMultiValue,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@crono-one/design-system';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import { TagInsert } from 'crono-fe-common/types/tagInsert';
import {
  getOptionsMultiselect,
  setOptionsMultiselect,
} from 'crono-fe-common/utils';

interface TemplateTagSelectProps {
  tag: ExternalProperty;
  value: string | null;
  onChange: (value: string | null) => void;
}

const TemplateTagSelect = ({
  tag,
  value,
  onChange,
}: TemplateTagSelectProps) => {
  const name = tag.publicName ?? tag.externalName ?? '';

  const items = useMemo(
    () =>
      (tag.options ?? []).map((option, index) => ({
        value: option,
        label: tag.labels?.[index] ?? option,
      })),
    [tag.options, tag.labels],
  );

  if (tag.valueType === 'MultiSelect') {
    return (
      <SelectMulti
        items={items}
        value={getOptionsMultiselect(value)}
        onValueChange={(options) => onChange(setOptionsMultiselect(options))}
      >
        <SelectMultiTrigger size="xs" aria-label={name}>
          {/* The trigger keeps showing the tag name so the row stays readable;
              the count chip next to it reports how many options are picked. */}
          <SelectMultiValue placeholder={name}>
            {(selected) =>
              selected.length === 1
                ? (items.find((item) => item.value === selected[0])?.label ??
                  name)
                : name
            }
          </SelectMultiValue>
        </SelectMultiTrigger>

        <SelectMultiContent>
          <SelectMultiGroup>
            {items.map((item) => (
              <SelectMultiItem key={item.value} value={item.value}>
                {item.label}
              </SelectMultiItem>
            ))}
          </SelectMultiGroup>
        </SelectMultiContent>
      </SelectMulti>
    );
  }

  return (
    <Select value={value ?? ''} onValueChange={onChange}>
      <SelectTrigger size="xs" aria-label={name}>
        <SelectValue placeholder={name} />
      </SelectTrigger>

      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>

        {value && (
          <>
            <SelectSeparator />

            <SelectGroup>
              <SelectActionItem onClick={() => onChange(null)}>
                Clear
              </SelectActionItem>
            </SelectGroup>
          </>
        )}
      </SelectContent>
    </Select>
  );
};

interface TemplateTagSelectsProps {
  tags: ExternalProperty[];
  selected: TagInsert[];
  onTagChange: (externalPropertyId: number, value: string | null) => void;
}

const TemplateTagSelects = ({
  tags,
  selected,
  onTagChange,
}: TemplateTagSelectsProps) => (
  <div className="flex flex-wrap gap-2">
    {tags.map((tag) => (
      <TemplateTagSelect
        key={tag.id}
        tag={tag}
        value={
          selected.find((entry) => entry.externalPropertyId === tag.id)
            ?.value ?? null
        }
        onChange={(value) => onTagChange(tag.id, value)}
      />
    ))}
  </div>
);

export default TemplateTagSelects;
